import { Injectable, signal, computed } from '@angular/core';
import {
  TipoOrgano,
  Municipio,
  Organo,
  Puesto,
  Rol,
  Sistema,
  Usuario,
  SistemaOrgano,
  SistemaUsuario,
  UsuarioOrgano,
} from '../models/types';

@Injectable({ providedIn: 'root' })
export class MockDataService {
  // Inject will be set after AuthService is available
  private currentUserOrganoId: (() => number | undefined) | null = null;
  private isSuperAdmin: (() => boolean) | null = null;

  setAuthContext(isSuperAdmin: () => boolean, organoId: () => number | undefined): void {
    this.isSuperAdmin = isSuperAdmin;
    this.currentUserOrganoId = organoId;
  }

  // Role-filtered computed data
  filteredUsuarios = computed(() => {
    const allUsers = this.usuarios();
    if (!this.isSuperAdmin || this.isSuperAdmin()) return allUsers;

    const organoId = this.currentUserOrganoId?.();
    if (!organoId) return allUsers;

    const userIdsInOrgano = new Set(
      this.usuariosOrganos()
        .filter((a) => a.idOrgano === organoId)
        .map((a) => a.idUsuario),
    );
    return allUsers.filter((u) => userIdsInOrgano.has(u.id));
  });

  filteredSistemas = computed(() => {
    const allSistemas = this.sistemas().filter((s) => s.estatus);
    if (!this.isSuperAdmin || this.isSuperAdmin()) return allSistemas;

    const organoId = this.currentUserOrganoId?.();
    if (!organoId) return allSistemas;

    const sistemaIds = new Set(
      this.sistemasOrganos()
        .filter((so) => so.idOrgano === organoId && so.estatus)
        .map((so) => so.idSistema),
    );
    return allSistemas.filter((s) => sistemaIds.has(s.id));
  });

  filteredUsuariosOrganos = computed(() => {
    const allAccesos = this.usuariosOrganos();
    if (!this.isSuperAdmin || this.isSuperAdmin()) return allAccesos;

    const organoId = this.currentUserOrganoId?.();
    if (!organoId) return allAccesos;

    return allAccesos.filter((a) => a.idOrgano === organoId);
  });
  // Tipos de Órgano
  readonly tiposOrgano = signal<TipoOrgano[]>([
    { id: 1, nombre: 'Dependencia', estatus: true },
    { id: 2, nombre: 'Entidad', estatus: true },
    { id: 3, nombre: 'Órgano Autónomo', estatus: true },
    { id: 4, nombre: 'Municipio', estatus: true },
    { id: 5, nombre: 'Poder Judicial', estatus: false },
  ]);

  // Municipios
  readonly municipios = signal<Municipio[]>([
    { id: 1, nombre: 'Aguascalientes', estatus: true },
    { id: 2, nombre: 'Jesús María', estatus: true },
    { id: 3, nombre: 'Calvillo', estatus: true },
    { id: 4, nombre: 'Rincón de Romos', estatus: true },
    { id: 5, nombre: 'Pabellón de Arteaga', estatus: true },
  ]);

  // Órganos
  readonly organos = signal<Organo[]>([
    {
      id: 1,
      nombre: 'Órgano Central',
      siglas: 'OC',
      idTipoOrgano: 1,
      idMunicipio: 1,
      estatus: true,
    },
    {
      id: 2,
      nombre: 'Órgano Norte',
      siglas: 'ON',
      idTipoOrgano: 2,
      idMunicipio: 2,
      estatus: true,
    },
    {
      id: 3,
      nombre: 'Órgano Sur',
      siglas: 'OS',
      idTipoOrgano: 1,
      idMunicipio: 3,
      estatus: true,
    },
    {
      id: 4,
      nombre: 'Órgano Este',
      siglas: 'OE',
      idTipoOrgano: 3,
      idMunicipio: 4,
      estatus: true,
    },
    {
      id: 5,
      nombre: 'Órgano Oeste',
      siglas: 'OO',
      idTipoOrgano: 2,
      idMunicipio: 5,
      estatus: true,
    },
  ]);

  // Puestos
  readonly puestos = signal<Puesto[]>([
    { id: 1, nombre: 'Director General', estatus: true },
    { id: 2, nombre: 'Subdirector', estatus: true },
    { id: 3, nombre: 'Jefe de Departamento', estatus: true },
    { id: 4, nombre: 'Analista', estatus: true },
    { id: 5, nombre: 'Auxiliar Administrativo', estatus: true },
    { id: 6, nombre: 'Coordinador', estatus: false },
  ]);

  // Roles
  readonly roles = signal<Rol[]>([
    {
      id: 1,
      nombre: 'Administrador',
      descripcion: 'Acceso total al sistema',
      estatus: true,
    },
    {
      id: 2,
      nombre: 'Secretaría Ejecutiva',
      descripcion: 'Personal de la Secretaría Ejecutiva',
      estatus: true,
    },
    {
      id: 3,
      nombre: 'Empleado',
      descripcion: 'Personal operativo general',
      estatus: true,
    },
    {
      id: 4,
      nombre: 'Empleado SE',
      descripcion: 'Personal operativo de Secretaría Ejecutiva',
      estatus: true,
    },
    {
      id: 5,
      nombre: 'Empleado JER',
      descripcion: 'Personal operativo de JER',
      estatus: true,
    },
    { id: 6, nombre: 'Titular', descripcion: 'Titular de área', estatus: true },
    {
      id: 7,
      nombre: 'Titular JER',
      descripcion: 'Titular de JER',
      estatus: true,
    },
    {
      id: 8,
      nombre: 'DDISPE',
      descripcion: 'Dirección de Desarrollo e Innovación',
      estatus: true,
    },
    {
      id: 9,
      nombre: 'Consulta',
      descripcion: 'Acceso de solo lectura',
      estatus: true,
    },
  ]);

  // Sistemas
  readonly sistemas = signal<Sistema[]>([
    {
      id: 1,
      nombre: 'Sistema de Permisos',
      nombreCorto: 'Permisos',
      ruta: 'https://solicitudpermisos.ieeg.mx',
      soloActivoProceso: false,
      estatus: true,
    },
    {
      id: 2,
      nombre: 'Sistema de Inventarios',
      nombreCorto: 'Inventarios',
      ruta: 'https://inventario.ieeg.mx',
      soloActivoProceso: false,
      estatus: true,
    },
    {
      id: 3,
      nombre: 'Sistema de Cómputos',
      nombreCorto: 'Computos',
      ruta: 'https://computos.ieeg.mx',
      soloActivoProceso: true,
      estatus: true,
    },
  ]);

  // Usuarios
  readonly usuarios = signal<Usuario[]>([
    {
      id: 1,
      numeroEmpleado: 1001,
      nombreEmpleado: 'Carlos Mendoza García',
      esMujer: false,
      correo: 'carlos.mendoza@empresa.com',
      alias: 'cmendoza',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
    {
      id: 2,
      numeroEmpleado: 1002,
      nombreEmpleado: 'María Elena Rodríguez',
      esMujer: true,
      correo: 'maria.rodriguez@empresa.com',
      alias: 'mrodriguez',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
    {
      id: 3,
      numeroEmpleado: 1003,
      nombreEmpleado: 'José Luis Fernández',
      esMujer: false,
      correo: 'jose.fernandez@empresa.com',
      alias: 'jfernandez',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
    {
      id: 4,
      numeroEmpleado: 1004,
      nombreEmpleado: 'Ana Patricia Sánchez',
      esMujer: true,
      correo: 'ana.sanchez@empresa.com',
      alias: 'asanchez',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
    {
      id: 5,
      numeroEmpleado: 1005,
      nombreEmpleado: 'Roberto Carlos López',
      esMujer: false,
      correo: 'roberto.lopez@empresa.com',
      alias: 'rlopez',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
    {
      id: 6,
      numeroEmpleado: 1006,
      nombreEmpleado: 'Laura Beatriz Martínez',
      esMujer: true,
      correo: 'laura.martinez@empresa.com',
      alias: 'lmartinez',
      tieneLicenciaCorreo: false,
      estatus: true,
    },
    {
      id: 7,
      numeroEmpleado: 1007,
      nombreEmpleado: 'Francisco Javier Torres',
      esMujer: false,
      correo: 'francisco.torres@empresa.com',
      alias: 'ftorres',
      tieneLicenciaCorreo: true,
      estatus: false,
    },
    {
      id: 8,
      numeroEmpleado: 1008,
      nombreEmpleado: 'Carmen Rosa Díaz',
      esMujer: true,
      correo: 'carmen.diaz@empresa.com',
      alias: 'cdiaz',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
    {
      id: 9,
      numeroEmpleado: 1009,
      nombreEmpleado: 'Miguel Ángel Vargas',
      esMujer: false,
      correo: 'miguel.vargas@empresa.com',
      alias: 'mvargas',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
    {
      id: 10,
      numeroEmpleado: 1010,
      nombreEmpleado: 'Sofía Isabel Moreno',
      esMujer: true,
      correo: 'sofia.moreno@empresa.com',
      alias: 'smoreno',
      tieneLicenciaCorreo: true,
      estatus: true,
    },
  ]);

  // Sistemas-Organos
  readonly sistemasOrganos = signal<SistemaOrgano[]>([
    { idSistema: 1, idOrgano: 1, estatus: true },
    { idSistema: 2, idOrgano: 1, estatus: true },
    { idSistema: 3, idOrgano: 1, estatus: true },
    { idSistema: 1, idOrgano: 2, estatus: true },
    { idSistema: 3, idOrgano: 2, estatus: true },
    { idSistema: 2, idOrgano: 3, estatus: true },
    { idSistema: 3, idOrgano: 3, estatus: true },
    { idSistema: 1, idOrgano: 4, estatus: true },
    { idSistema: 2, idOrgano: 4, estatus: true },
    { idSistema: 3, idOrgano: 5, estatus: true },
  ]);

  // Sistemas-Usuarios
  readonly sistemasUsuarios = signal<SistemaUsuario[]>([
    { idSistema: 1, idUsuario: 1, estatus: true },
    { idSistema: 2, idUsuario: 1, estatus: true },
    { idSistema: 3, idUsuario: 1, estatus: true },
    { idSistema: 1, idUsuario: 2, estatus: true },
    { idSistema: 3, idUsuario: 2, estatus: true },
    { idSistema: 2, idUsuario: 3, estatus: true },
    { idSistema: 3, idUsuario: 3, estatus: true },
    { idSistema: 1, idUsuario: 4, estatus: true },
    { idSistema: 2, idUsuario: 5, estatus: true },
    { idSistema: 3, idUsuario: 6, estatus: true },
  ]);

  // Usuarios-Organos (Access Matrix)
  readonly usuariosOrganos = signal<UsuarioOrgano[]>([
    {
      idSistema: 1,
      idUsuario: 1,
      idOrgano: 1,
      idRol: 1,
      idPuesto: 1,
      estatus: true,
    },
    {
      idSistema: 2,
      idUsuario: 1,
      idOrgano: 1,
      idRol: 1,
      idPuesto: 1,
      estatus: true,
    },
    {
      idSistema: 3,
      idUsuario: 1,
      idOrgano: 1,
      idRol: 1,
      idPuesto: 1,
      estatus: true,
    },
    {
      idSistema: 1,
      idUsuario: 2,
      idOrgano: 2,
      idRol: 2,
      idPuesto: 2,
      estatus: true,
    },
    {
      idSistema: 3,
      idUsuario: 2,
      idOrgano: 2,
      idRol: 2,
      idPuesto: 2,
      estatus: true,
    },
    {
      idSistema: 2,
      idUsuario: 3,
      idOrgano: 3,
      idRol: 3,
      idPuesto: 3,
      estatus: true,
    },
    {
      idSistema: 3,
      idUsuario: 3,
      idOrgano: 3,
      idRol: 3,
      idPuesto: 3,
      estatus: true,
    },
    {
      idSistema: 1,
      idUsuario: 4,
      idOrgano: 2,
      idRol: 4,
      idPuesto: 4,
      estatus: true,
    },
    {
      idSistema: 2,
      idUsuario: 5,
      idOrgano: 4,
      idRol: 5,
      idPuesto: 5,
      estatus: true,
    },
    {
      idSistema: 3,
      idUsuario: 6,
      idOrgano: 5,
      idRol: 6,
      idPuesto: 1,
      estatus: true,
    },
    {
      idSistema: 1,
      idUsuario: 7,
      idOrgano: 1,
      idRol: 7,
      idPuesto: 2,
      estatus: false,
    },
    {
      idSistema: 2,
      idUsuario: 7,
      idOrgano: 1,
      idRol: 7,
      idPuesto: 2,
      estatus: false,
    },
    {
      idSistema: 1,
      idUsuario: 8,
      idOrgano: 1,
      idRol: 8,
      idPuesto: 3,
      estatus: true,
    },
    {
      idSistema: 2,
      idUsuario: 8,
      idOrgano: 1,
      idRol: 8,
      idPuesto: 3,
      estatus: true,
    },
    {
      idSistema: 3,
      idUsuario: 8,
      idOrgano: 1,
      idRol: 8,
      idPuesto: 3,
      estatus: true,
    },
    {
      idSistema: 1,
      idUsuario: 9,
      idOrgano: 4,
      idRol: 9,
      idPuesto: 4,
      estatus: true,
    },
    {
      idSistema: 3,
      idUsuario: 9,
      idOrgano: 4,
      idRol: 9,
      idPuesto: 4,
      estatus: true,
    },
    {
      idSistema: 2,
      idUsuario: 10,
      idOrgano: 3,
      idRol: 9,
      idPuesto: 5,
      estatus: true,
    },
    {
      idSistema: 3,
      idUsuario: 10,
      idOrgano: 3,
      idRol: 9,
      idPuesto: 5,
      estatus: true,
    },
  ]);

  // Lookup methods
  getOrganoById(id: number): Organo | undefined {
    return this.organos().find((o) => o.id === id);
  }

  getSistemaById(id: number): Sistema | undefined {
    return this.sistemas().find((s) => s.id === id);
  }

  getUsuarioById(id: number): Usuario | undefined {
    return this.usuarios().find((u) => u.id === id);
  }

  getRolById(id: number): Rol | undefined {
    return this.roles().find((r) => r.id === id);
  }

  getPuestoById(id: number): Puesto | undefined {
    return this.puestos().find((p) => p.id === id);
  }

  getTipoOrganoById(id: number): TipoOrgano | undefined {
    return this.tiposOrgano().find((t) => t.id === id);
  }

  getMunicipioById(id: number): Municipio | undefined {
    return this.municipios().find((m) => m.id === id);
  }

  // CRUD Methods
  createUser(user: Omit<Usuario, 'id'>): void {
    const newId = Math.max(...this.usuarios().map((u) => u.id), 0) + 1;
    this.usuarios.update((users) => [...users, { ...user, id: newId }]);
  }

  updateUser(id: number, userData: Partial<Usuario>): void {
    this.usuarios.update((users) => users.map((u) => (u.id === id ? { ...u, ...userData } : u)));
  }

  deleteUser(id: number): void {
    this.usuarios.update((users) => users.filter((u) => u.id !== id));
  }
}
